import { createElement, useCallback, useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { parse, serialize } from 'cookie';
import * as Sentry from '@sentry/remix';
import type { FrontendData } from '~/types/figma-simplified-api';
import {
  figmaTokenStore,
  figmaDesignStore,
  figmaUIStore,
  attachedImagesStore,
  figmaConnectionStatusStore,

  // Types
  type AttachedImage,
  type FigmaTokenState,
  type FigmaDesignState,
  type FigmaUIState,
  type FigmaConnectionStatus,

  // Functions
  initializeFigmaToken,
  setFigmaToken,
  clearFigmaToken,
  setFigmaTokenValidation,
  setFigmaDesign,
  setFigmaDesignError,
  clearFigmaDesign,
  setFigmaUrl,
  openFigmaModal,
  closeFigmaModal,
  setFigmaExtracting,
  setFigmaExtractionDone,
  setFigmaValidatingToken,
  addAttachedImages,
  compressImage,
  removeAttachedImage,
  removeAttachedImageByIndex,
  clearAllAttachedImages,
  getNonFigmaImages,
  getFigmaImage,
  canAttachMoreImages,
  resetAllFigmaState,
  restoreAttachedImages,
} from '@/lib/stores/figmaAndImages';
import {
  projectAssetsStore,
  areAssetsLoading as areAssetsLoadingStore,
  addProjectAssets,
  removeProjectAsset,
  clearProjectAssets,
  type ProjectAsset,
  setAssetsFromDB,
} from '~/lib/stores/assets';
import { MAX_JSON_TOKENS, MAX_ATTACHED_IMAGES, MAX_ASSET_IMAGE_SIZE, MAX_PROJECT_ASSETS } from '~/utils/constants';
import type { ApiResponse, ApiKeys } from '~/utils/OauthHelper';
import type { AssetUploads } from '@/services/dbSchema';
import { useFetcher } from '@remix-run/react';

// ============================================================================
// TYPES
// ============================================================================

export interface UseFigmaAndAttachmentsReturn {
  // State (reactive via useStore)
  tokenState: FigmaTokenState;
  designState: FigmaDesignState;
  uiState: FigmaUIState;
  attachedImages: AttachedImage[];
  connectionStatus: FigmaConnectionStatus;

  // Computed values
  hasFigmaDesign: boolean;
  hasFigmaError: boolean;
  isExtracting: boolean;
  canAttachImages: boolean;
  figmaImageBase64: string | null;
  nonFigmaImages: AttachedImage[];
  isAttachingImages: boolean;

  // Token operations
  validateToken: () => Promise<boolean>;
  initiateOAuth: (clientId: string, redirectUri: string) => Promise<void>;
  disconnectFigma: () => Promise<void>;
  hasValidToken: () => Promise<boolean>;

  // Design operations
  extractDesign: (figmaUrl: string) => Promise<void>;
  detachDesign: () => void;
  cancelExtraction: () => void;

  // Image operations
  attachImages: (images: string[], source: 'upload' | 'paste' | 'figma') => Promise<boolean>;
  setAttachedImagesInStore: (images: AttachedImage[]) => void;
  removeImage: (imageId: string) => void;
  removeImageByIndex: (index: number) => void;
  clearAllImages: () => void;
  clearFigmaDesignAndImages: () => void;

  // Asset operations
  assets: ProjectAsset[];
  areAssetsLoading: boolean;
  uploadAssets: (files: File[], projectId: string) => Promise<boolean>;
  removeAsset: (id: string) => void;
  clearAssets: () => void;
  setAssetsStore: (assets: AssetUploads[]) => void;
  canAttachAssets: boolean;
  hasAssets: boolean;

  // Modal operations
  openModal: (resetState?: boolean) => void;
  closeModal: () => void;

  // Utility
  getDesignForPrompt: () => { design: FrontendData | null; figmaUrl: string; images: string[] };
  reset: () => Promise<void>;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const FIGMA_RATE_LIMITS_URL = 'https://developers.figma.com/docs/rest-api/rate-limits/';
const FIGMA_RATE_LIMIT_TOAST_DURATION_MS = 15_000;

type FigmaApiErrorResponse = {
  type: 'failed';
  error: string;
  code?: string;
  retryable?: boolean;
  upstreamStatus?: number;
};

const showFigmaRateLimitToast = (): void => {
  toast.error(
    createElement(
      'div',
      null,
      createElement('div', null, 'Your Figma account has reached its API rate limit. Please wait and try again.'),
      createElement(
        'a',
        {
          href: FIGMA_RATE_LIMITS_URL,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: { display: 'inline-block', marginTop: '6px', textDecoration: 'underline' },
        },
        'View Figma API rate limits',
      ),
    ),
    {
      autoClose: FIGMA_RATE_LIMIT_TOAST_DURATION_MS,
      closeOnClick: false,
    },
  );
};

const getFigmaExtractionErrorMessage = (errorData?: FigmaApiErrorResponse): string => {
  switch (errorData?.code) {
    case 'FIGMA_AUTHENTICATION_FAILED': {
      return 'Your Figma connection has expired. Please reconnect your account.';
    }
    case 'FIGMA_ACCESS_DENIED': {
      return 'Figma denied access to this design. Check that it is shared with your connected account.';
    }
    case 'FIGMA_RESOURCE_NOT_FOUND': {
      return 'The selected Figma design or frame could not be found.';
    }
    case 'FIGMA_TIMEOUT': {
      return 'Figma took too long to respond. Please try again.';
    }
    default: {
      return errorData?.error || 'Failed to extract design. Please try again.';
    }
  }
};

const validateFigmaToken = async (token: string): Promise<{ isValid: boolean; userData?: any }> => {
  try {
    const response = await axios.get('https://api.figma.com/v1/me', {
      headers: { Authorization: `Bearer ${token}` },
    });

    return { isValid: response.status === 200, userData: response.data };
  } catch (error) {
    let status: number | undefined;

    if (axios.isAxiosError(error) && error.response) {
      status = error.response.status;

      switch (status) {
        case 401: {
          toast.error('Unauthorized: Invalid Figma token. Please reconnect your account.');
          break;
        }
        case 403: {
          toast.error('Forbidden: Access denied. Please check your permissions.');
          break;
        }
        case 429: {
          showFigmaRateLimitToast();
          break;
        }
        default: {
          toast.error('An error occurred validating your Figma connection.');
          break;
        }
      }
    }

    if (!status || status >= 500) {
      Sentry.captureException(new Error('Figma token validation failed'), {
        extra: {
          status,
          code: axios.isAxiosError(error) ? error.code : undefined,
        },
      });
    }

    return { isValid: false };
  }
};

export const validateFigmaUrl = (url: string): { isValid: boolean; error: string | null } => {
  if (!url.trim()) {
    return { isValid: false, error: null };
  }

  try {
    new URL(url);
  } catch {
    return { isValid: false, error: 'Please enter a valid URL' };
  }

  if (!url.startsWith('https://www.figma.com/')) {
    return { isValid: false, error: 'Please enter a valid Figma URL' };
  }

  const urlPattern = /design\/([^/]+).*node-id=([^&]+)/;

  if (!urlPattern.test(url)) {
    return { isValid: false, error: 'Please use a Figma design URL with a selected frame (node-id required)' };
  }

  return { isValid: true, error: null };
};

export const isValidAssetImageSize = (image: string): boolean => {
  return image.length <= MAX_ASSET_IMAGE_SIZE;
};

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useFigmaAndAttachments(): UseFigmaAndAttachmentsReturn {
  // Subscribe to all stores
  const tokenState = useStore(figmaTokenStore);
  const designState = useStore(figmaDesignStore);
  const uiState = useStore(figmaUIStore);
  const attachedImages = useStore(attachedImagesStore);
  const connectionStatus = useStore(figmaConnectionStatusStore);
  const assets = useStore(projectAssetsStore);
  const areAssetsLoading = useStore(areAssetsLoadingStore);
  const fetcher = useFetcher<{ tokens: Record<string, any> }>();

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const isOpen = figmaUIStore.get().isModalOpen;

    if (!isOpen) {
      return;
    }

    if (fetcher.state === 'idle' && !fetcher.data) {
      const token = parse(document.cookie).dualite_figma_token || '';
      fetcher.submit(
        { provider: 'figma', localAccessToken: token },
        { method: 'POST', action: '/api/access-token', encType: 'application/json' },
      );
    }
  }, [fetcher, figmaUIStore.get().isModalOpen]);

  useEffect(() => {
    const isOpen = figmaUIStore.get().isModalOpen;

    if (!isOpen) {
      return;
    }

    if (fetcher.data?.tokens) {
      const { figma } = fetcher.data.tokens;
      console.log('[int] fetching token for figma: ', figma);

      if (figma) {
        document.cookie = serialize('dualite_figma_token', figma.accessToken, {
          path: '/',
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
          maxAge: 30 * 24 * 60 * 60, // 30 days
        });
      }
    }

    initializeFigmaToken();
  }, [fetcher.data, figmaUIStore.get().isModalOpen]);

  // Computed values
  const hasFigmaDesign = designState.design !== null && !designState.hasError;
  const hasFigmaError = designState.hasError;
  const isExtracting = uiState.isExtracting;
  const canAttachImages = canAttachMoreImages(MAX_ATTACHED_IMAGES);
  const figmaImageBase64 = designState.design?.image ?? null;
  const nonFigmaImages = getNonFigmaImages();
  const isAttachingImages = attachedImages.some((img) => img.isAttaching);
  const hasAssets = assets.some((asset) => asset.isReferenced);

  // ----- Token Operations -----

  const validateToken = useCallback(async (): Promise<boolean> => {
    // Get token from cookie
    const cookies = parse(document.cookie);
    const token = cookies.dualite_figma_token;

    if (!token) {
      setFigmaTokenValidation(false);
      return false;
    }

    setFigmaValidatingToken(true);

    try {
      const { isValid, userData } = await validateFigmaToken(token);

      if (isValid && userData) {
        setFigmaToken(token, userData);
      } else {
        await clearFigmaToken();
      }

      return isValid;
    } finally {
      setFigmaValidatingToken(false);
    }
  }, []);

  const hasValidToken = useCallback(async (): Promise<boolean> => {
    return tokenState.accessToken ? await validateToken() : tokenState.isValid;
  }, [tokenState.accessToken, tokenState.isValid, validateToken]);

  const initiateOAuth = useCallback(
    async (clientId: string, redirectUri: string): Promise<void> => {
      try {
        const {
          data: { data: keys },
        } = await axios.post<ApiResponse<ApiKeys>>('/api/auth');

        if (keys) {
          // Store keys in cookie (5 minutes)
          document.cookie = serialize('dualite_api_keys', JSON.stringify(keys), {
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'lax',
            maxAge: 300,
          });

          const oauthUrl = `https://www.figma.com/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=file_content:read file_metadata:read current_user:read&state=${keys.writeKey}&response_type=code`;

          window.open(oauthUrl, '_blank');

          // Start polling for token
          let attempts = 0;
          const maxAttempts = 15;

          const pollInterval = setInterval(async () => {
            // Check cookie for token
            const cookies = parse(document.cookie);
            const token = cookies.dualite_figma_token;

            if (token) {
              clearInterval(pollInterval);
              await validateToken();
              toast.success('Successfully connected to Figma');
            } else {
              attempts++;

              if (attempts >= maxAttempts) {
                clearInterval(pollInterval);
                toast.error('Failed to connect to Figma. Please try again.');
              }
            }
          }, 3000);
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: { message: 'Error initiating Figma OAuth' },
        });
        toast.error('Failed to initiate Figma connection');
      }
    },
    [validateToken],
  );

  const disconnectFigma = useCallback(async (): Promise<void> => {
    const confirmed = window.confirm('Are you sure you want to disconnect your Figma account?');

    if (confirmed) {
      cancelExtraction();
      await resetAllFigmaState();
      toast.success('Figma account has been disconnected successfully');
    }
  }, []);

  // ----- Design Operations -----

  const extractDesign = useCallback(
    async (figmaUrl: string): Promise<void> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const signal = controller.signal;

      setFigmaExtracting(true);

      const validation = validateFigmaUrl(figmaUrl);

      if (!validation.isValid) {
        if (validation.error) {
          toast.error(validation.error);
        }

        setFigmaExtracting(false);
        return;
      }

      // Get token from cookie
      const cookies = parse(document.cookie);
      const token = cookies.dualite_figma_token;

      if (!token) {
        toast.error('Please connect your Figma account first');
        setFigmaExtracting(false);
        return;
      }

      // Validate token before extracting
      const isValid = await validateToken();

      if (!isValid) {
        setFigmaExtracting(false);
        return;
      }

      setFigmaUrl(figmaUrl);

      try {
        const urlPattern = /design\/([^/]+).*node-id=([^&]+)/;
        const matches = figmaUrl.match(urlPattern);

        if (!matches) {
          throw new Error('Invalid Figma URL format');
        }

        const [, fileId, rawNodeId] = matches;

        const response = await axios.post<FrontendData>(
          '/api/figma',
          { designFileLink: figmaUrl, nodeId: rawNodeId },
          { headers: { Authorization: `Bearer ${token}` }, signal },
        );

        const data = response.data;

        if (signal.aborted) {
          return;
        }

        if (!data) {
          throw new Error('Failed to extract design');
        }

        // Check JSON size limit
        const { image, ...rest } = data;
        const jsonString = JSON.stringify(rest);

        if (jsonString.length / 4 > MAX_JSON_TOKENS) {
          setFigmaDesignError('jsonSize', image ?? undefined);
          toast.error('Design data is too large. Please select a smaller design.');
          return;
        }

        if (image) {
          try {
            if (signal.aborted) {
              return;
            }

            // set figma data with raw image
            setFigmaDesign(data, figmaUrl);

            const { base64: compressedImg } = await compressImage(image, 'figma', signal);

            if (signal.aborted) {
              return;
            }

            // update figma data with compressed image
            const newData: FrontendData = {
              ...data,
              image: compressedImg,
            };
            setFigmaDesign(newData, figmaUrl);
          } catch (error) {
            if ((error instanceof Error && error.message === 'Extraction cancelled') || signal.aborted) {
              return;
            }

            console.error('[handleAssetUpload] Asset upload error: ', error);
            Sentry.captureException(error, {
              extra: {
                message: 'Error occured in extract design while uploading image to GCS or downloading image to base64',
              },
            });
            toast.error('Failed to extract design');
            return;
          }
        } else {
          if (signal.aborted) {
            return;
          }

          setFigmaDesign(data, figmaUrl);
        }

        // Check image size and dimensions
        // const imageSizeInMB = data.imageSizeInMB;

        // if (image) {
        //   const dimensions = await getImageDimensions(image);

        //   if (imageSizeInMB > MAX_SIZE) {
        //     setFigmaDesignError('size', image);
        //     toast.error('Design is too large to compress. Please select a smaller design.');
        //     return;
        //   }

        //   if (dimensions.width > MAX_DIMENSION || dimensions.height > MAX_DIMENSION) {
        //     setFigmaDesignError('dimension', image);
        //     toast.error(`Image dimensions exceed ${MAX_DIMENSION}px. Please select a smaller design.`);
        //     return;
        //   }

        //   // Check if compression is needed
        //   const longestDimension = Math.max(dimensions.width, dimensions.height);
        //   const autoScale = Math.max(TARGET_MIN_SCALE, Math.min(1, TARGET_LONGEST_EDGE / longestDimension));

        //   if (autoScale < 0.999) {
        //     // Compress the image
        //     try {
        //       const compressResponse = await axios.post('/api/compress-figma-image', {
        //         base64Image: image,
        //         compressedScale: autoScale,
        //       });

        //       const { compressedBase64Image } = compressResponse.data;

        //       setFigmaDesign({ ...data, image: compressedBase64Image }, figmaUrl);
        //     } catch (compressError) {
        //       // Use original if compression fails
        //       setFigmaDesign(data, figmaUrl);
        //     }
        //   } else {
        //     setFigmaDesign(data, figmaUrl);
        //   }
        // } else {
        //   setFigmaDesign(data, figmaUrl);
        // }

        setFigmaExtractionDone(true);
        toast.success('Design assets extracted successfully');
      } catch (error) {
        if (axios.isCancel(error) || (error instanceof Error && error.name === 'AbortError')) {
          console.log('Operation cancelled by user');
          return;
        }

        if (axios.isAxiosError<FigmaApiErrorResponse>(error)) {
          const status = error.response?.status;
          const errorData = error.response?.data;

          if (status === 429 || errorData?.code === 'FIGMA_RATE_LIMITED') {
            showFigmaRateLimitToast();
          } else {
            toast.error(getFigmaExtractionErrorMessage(errorData));
          }

          if (!status || status >= 500) {
            Sentry.captureException(new Error('Figma design extraction request failed'), {
              extra: {
                status,
                code: errorData?.code || error.code,
                retryable: errorData?.retryable,
              },
            });
          }
        } else {
          Sentry.captureException(new Error('Unexpected Figma design extraction error'), {
            extra: {
              message: error instanceof Error ? error.message : 'Unknown error',
            },
          });
          toast.error('Failed to extract design. Please try again.');
        }

        clearFigmaDesign();
      } finally {
        if (abortControllerRef.current === controller) {
          setFigmaExtracting(false);
          abortControllerRef.current = null;
        }
      }
    },
    [validateToken],
  );

  const detachDesign = useCallback((): void => {
    clearFigmaDesign();
    setFigmaExtractionDone(false);
  }, []);

  const cancelExtraction = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setFigmaExtracting(false);
  }, []);

  // ----- Image Operations -----

  const attachImages = useCallback(async (images: string[], source: 'upload' | 'paste' | 'figma'): Promise<boolean> => {
    // Check if Figma design is attached
    if (getFigmaImage()) {
      toast.error('Please remove the Figma design first to attach images.');
      return false;
    }

    const currentCount = getNonFigmaImages().length;
    const totalAfter = currentCount + images.length;

    if (totalAfter > MAX_ATTACHED_IMAGES) {
      const remaining = MAX_ATTACHED_IMAGES - currentCount;

      if (remaining <= 0) {
        toast.error(`You can only attach up to ${MAX_ATTACHED_IMAGES} images at a time.`);
      } else {
        toast.error(
          `You can only attach up to ${MAX_ATTACHED_IMAGES} images at a time. You can add ${remaining} more image(s).`,
        );
      }

      return false;
    }

    await addAttachedImages(images, source);
    return true;
  }, []);

  const setAttachedImagesInStore = useCallback((images: AttachedImage[]): void => {
    restoreAttachedImages(images);
  }, []);

  const removeImage = useCallback((imageId: string): void => {
    removeAttachedImage(imageId);
  }, []);

  const removeImageByIndex = useCallback((index: number): void => {
    removeAttachedImageByIndex(index);
  }, []);

  const clearAllImages = useCallback((): void => {
    clearAllAttachedImages();
  }, []);

  const clearFigmaDesignAndImages = useCallback((): void => {
    clearFigmaDesignAndImages();
  }, []);

  // ----- Asset Operations -----
  const uploadAssets = useCallback(
    async (files: File[], projectId: string): Promise<boolean> => {
      try {
        await addProjectAssets(files, projectId);
        return true;
      } catch (error) {
        return false;
      }
    },
    [addProjectAssets],
  );

  const setAssetsStore = useCallback((assets: AssetUploads[]) => {
    setAssetsFromDB(assets);
  }, []);

  const removeAsset = useCallback((id: string) => {
    removeProjectAsset(id);
  }, []);

  const clearAssets = useCallback(() => {
    clearProjectAssets();
  }, []);

  const canAttachAssets = assets.length < MAX_PROJECT_ASSETS;

  // ----- Modal Operations -----

  const openModal = useCallback((resetState: boolean = false): void => {
    openFigmaModal(resetState);
  }, []);

  const closeModal = useCallback((): void => {
    closeFigmaModal();
  }, []);

  // ----- Utility -----

  const getDesignForPrompt = useCallback((): {
    design: FrontendData | null;
    figmaUrl: string;
    images: string[];
  } => {
    const design = hasFigmaDesign ? designState.design : null;

    // Prioritize base64 over gcsURL for API transmission (AI understands base64)
    const images = attachedImages.map((img) => img.base64 || img.gcsURL || '');
    const figmaUrl = designState.figmaUrl;

    return { design, figmaUrl, images };
  }, [hasFigmaDesign, designState.design, attachedImages]);

  const reset = useCallback(async (): Promise<void> => {
    await resetAllFigmaState();
  }, []);

  return {
    // State
    tokenState,
    designState,
    uiState,
    attachedImages,
    connectionStatus,

    // Computed
    hasFigmaDesign,
    hasFigmaError,
    isExtracting,
    canAttachImages,
    figmaImageBase64,
    nonFigmaImages,
    isAttachingImages,

    // Token operations
    validateToken,
    initiateOAuth,
    disconnectFigma,
    hasValidToken,

    // Design operations
    extractDesign,
    detachDesign,
    cancelExtraction,

    // Image operations
    attachImages,
    setAttachedImagesInStore,
    removeImage,
    removeImageByIndex,
    clearAllImages,
    clearFigmaDesignAndImages,

    // Asset operations
    assets,
    areAssetsLoading,
    uploadAssets,
    removeAsset,
    clearAssets,
    setAssetsStore,
    canAttachAssets,
    hasAssets,

    // Modal operations
    openModal,
    closeModal,

    // Utility
    getDesignForPrompt,
    reset,
  };
}
